import { observable } from 'mobx';
import { toNumber } from '../utils/numbers';
import logger from '../utils/logger';
import { DeviceTypes } from '../constants/DeviceModels';
import { WeighedMaterialType } from '../constants/MaterialModels';
import { MixerType } from '../constants/BatchTypeEnums';
import { OUTPUT_TTL } from '../constants/Pac';
import {
  parseInputs,
  parseOutputs,
} from '../utils/pac-data-converter/pac-data-converter';
import { isFeatureEnabled } from '../utils/feature-flags';
import {
  createTimerRegistry,
  timerKey,
} from '../utils/timer-manager/timer-manager';

const FEATURE_EXTENDED_ANALOG_INPUTS = isFeatureEnabled('extended_analog_inputs');

class PacSimulatorStore {
  @observable
    data = {};

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.stopFeedDevices = [];
    this.stopDischargeScales = [];
    this.timers = createTimerRegistry('PacSimulator');
    this.init();
    this.reset();
    this.dischargeSpeed = {};
  }

  static get initialData() {
    const data = {
      inputs: '0000000000000000.0000000000000000.0000000000000000', // 1=empty, 0=not empty
      meters: '000000000000000000000000000000000000000000000000 1000 1000 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0' +
        ' 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0',
      outputs: '0000000000000000.0000000000000000.0000000000000000.0000000000000000.0000000000000000.0000000000000000',
      ticks: 0,
      weights: '0000000000000000 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0',
      'analog-inputs': FEATURE_EXTENDED_ANALOG_INPUTS ? '0 0 0 0 0 0 0 0' : '0 0 0 0',
      'analog-outputs': '0 0 0 0',
    };
    return Object.assign({}, data);
  }

  setStartInputs() {
    if (!this.rootStore.materialStore || !this.rootStore.materialStore.materialDetailsLoaded) {
      this.timers.set(
        'start-inputs-retry',
        () => this.setStartInputs(),
        1000,
        'Retry loading material store for start inputs'
      );
      return;
    }

    const ports = [];
    const outputs = Array(96).fill('0');

    this.scales.forEach(scale => {
      if (scale.closeGateOnePNo) {
        ports.push(scale.closeGateOnePNo);
      }
      if (this.isWaterScale(scale)) {
        ports.push(scale.openGateOnePNo);
      }
      if (scale.closeGateTwoPNo) {
        ports.push(scale.closeGateTwoPNo);
      }
      if (scale.closeGateThreePNo) {
        ports.push(scale.closeGateThreePNo);
      }
    });

    let data = '';
    outputs.forEach((v, i) => {
      if (ports.includes(i+1)) {
        data += `${i + 1}=0 `;
      }
    });

    if (data) {
      this.runWS(`set-outputs ${data}`);
    }
  }

  reset() {
    this.stopFeedDevices = [];
    this.stopDischargeScales = [];

    // Clear all timers managed by timer registry
    this.timers.clearAll();

    // Reset weight change flags
    this.scales.forEach(scale => {
      this[`change-weight-${scale.inputPNo}`] = false;
    });

    this.setMoisture();
    this.setWeightForDecumScales();
    this.setStartInputs();
  }

  init(data = PacSimulatorStore.initialData) {
    this.data = data;
    this.runTicks();
  }

  isWSOpen() {
    return true;
  }

  get scales() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.Scale) || [];
  }

  get bins() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.Bin) || [];
  }

  get pumps() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.Pump) || [];
  }

  get belts() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.Belt) || [];
  }

  get tanks() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.Tank) || [];
  }

  get mixers() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.Mixer) || [];
  }

  get switches() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.Switch) || [];
  }
  get screws() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.Screw) || [];
  }

  get analogInputs() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.AnalogInput) || [];
  }

  get analogSwitches() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.AnalogSwitch) || [];
  }

  get boots() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.Boot) || [];
  }

  get horns() {
    return this.rootStore.devicesStore.devices.filter(v => v.deviceTypeName === DeviceTypes.Horn) || [];
  }

  setMoisture() {
    const min = 0;
    const max = 10;

    this.analogInputs.forEach(v => {
      const random = toNumber(Math.random() * (max - min) + min);
      this.setAnalogInput(v.channelPNo, random);
    });
  }

  setWeightForDecumScales(value = 0) {
    this.scales.forEach(v => {
      if (v.decumulative) {
        if (value) {
          this.setWeightPlus(v.inputPNo, value);
        } else {
          this.setWeight(v.inputPNo, 5000);
        }
      }
    });
  }

  isScaleOpen(scale, gateStatusPort) {
    return !!scale && toNumber(this.getInputValue(gateStatusPort || scale.gateOneStatusPNo)) === 0;
  };

  isWaterScale(scale) {
    const materialType = this.rootStore.devicesStore.getMaterialTypeForScale(scale);
    return materialType === WeighedMaterialType.Water;
  }

  runWS(command) {
    return this.run(command);
  }

  runTicks() {
    this.timers.set(
      timerKey.ticks(),
      () => {
        this.data.ticks += 1;
        this.runTicks();
      },
      1000,
      'Increment simulator ticks'
    );
  }

  run(command) {
    const commandName = command.split(' ')[0];
    let d = [];
    let port = 0;
    let value = 0;

    switch(commandName) {
      case 'get-status':
        // return current data
        break;
      case 'set-output':
        d = command.split(' ')[1];
        port = d ? d.split('=')[0] : 0;
        value = d ? d.split('=')[1] : 0;
        this.setOutputData(port, value);
        break;
      case 'set-outputs':
        d = command.split(' ');
        d.forEach((v, i) => {
          if (i > 0) {
            port = d[i].split('=')[0];
            value = d[i].split('=')[1];
            this.setOutputData(port, value);
          }
        });
        break;
      case 'set-weight':
        d = command.split(' ');
        if (d[1] === '+') {
          this.changeWeight(d[2], d[3], d[4], d[5], d[6]);
        } else {
          this.changeWeightForDecum(d[2], d[4], d[6]);
        }
        break;
      case 'reset-weight':
        d = command.split(' ');
        this.setWeightStatus(d[1]);
        this.timers.clear(timerKey.scaleWeight(d[1]));
        break;
      case 'set-analog':
        d = command.split(' ');
        this.animateAnalogOutput(d[1], toNumber(d[2]));
        break;
      case 'reset-indicator':
        d = command.split(' ');
        this.resetIndicator(d[1]);
        break;
      case 'reset-counter':
        d = command.split(' ');
        this.resetCounter(d[1]);
        break;
      case 'reset-meter':
        d = command.split(' ');
        this.resetMeter(d[1]);
        this.timers.clear(timerKey.pumpMeter(d[1]));
        break;
      case 'set-meter':
        d = command.split(' ');
        if (!d[1]) {
          return;
        }

        const part1 = d[1].split('=');
        const part2 = d[2] ? d[2].split('=') : [0,0];

        port = Number(part1[0]);
        const pump = this.pumps.find(v => port === v.meterPNo) || {};
        const pumpPort = Number(part2[0]) || pump.gatePNo;
        value = Number(part1[1]) || 0;
        this.resumeFeed(pump);
        this.setInputs(pump.emptyStatusPNo, 0);
        this.setMeterStatus(port, false);
        this.changeMeter(port, value, pumpPort, pump.emptyStatusPNo);
        break;
      default:
        // no default
    }
    let data = Object.assign({}, this.data);

    //new pac will send inputs and outputs as 32bit integer numbers, we can simulate it here
    const simulateInputsAndOutputsAs32Integers = false;
    if (simulateInputsAndOutputsAs32Integers) {
      data = {
        ...data,
        inputs: parseInputs(data.inputs),
        outputs: parseOutputs(data.outputs),
      };
    }

    if (commandName !== 'get-status') {
      logger.debug('pac demo:', command, data);

      this.rootStore.batchStore.saveLog(command);
    }

    return Promise.resolve(data);
  }

  setOutputData(port, value) {

    if (Number(value) > 0 || value === '!') {// open, change
      if (value === '!') {
        value = 3600000;
      } else {
        let horn = this.horns.find(v => v.switchPNo === Number(port));
        if (horn) {
          this.timers.set(
            timerKey.port(port),
            () => this.setOutput(port, '0'),
            toNumber(value),
            `Horn auto-off for port ${port}`
          );
        }
      }
      this.setOutput(port, '1');
      let scale = this.scales.find(v => v.openGateOnePNo === Number(port) || v.openGateOneAdditionalPNo === Number(port));
      if (scale) {
        this.setInputs(scale.gateOneStatusPNo, 0);
        if (Number(value) > 0 && !this[`change-weight-${scale.inputPNo}`]) {
          this.resumeDischarge(scale);
          this.discharge(scale.inputPNo, scale, scale.gateOneStatusPNo, Number(value), port);
        }
        return;
      }
      scale = this.scales.find(v => v.closeGateOnePNo === Number(port) || v.closeGateOneAdditionalPNo === Number(port));
      if (scale && Number(value) > 1000) {
        this.stopDischarge(scale);
        this.setInputs(scale.gateOneStatusPNo, 1);
        return;
      } else if (scale && !scale.decumulative && Number(value) < 1000) { // control flow rate
        this.discharge(scale.inputPNo, scale, scale.gateOneStatusPNo, Number(value), port);
        return;
      }

      scale = this.scales.find(v => v.openGateTwoPNo === Number(port) || v.openGateTwoAdditionalPNo === Number(port));
      if (scale) {
        this.setInputs(scale.gateTwoStatusPNo, 0);
        if (Number(value) > 0 && !this[`change-weight-${scale.inputPNo}`]) {
          this.resumeDischarge(scale);
          this.discharge(scale.inputPNo, scale, scale.gateTwoStatusPNo, Number(value));
        }
        return;
      }
      scale = this.scales.find(v => v.closeGateTwoPNo === Number(port) || v.closeGateTwoAdditionalPNo === Number(port));
      if (scale && Number(value) > 1000) { // ignoring closing the gate for less than 1 sec
        this.stopDischarge(scale);
        this.setInputs(scale.gateTwoStatusPNo, 1);
        return;
      }

      scale = this.scales.find(v => v.openGateThreePNo === Number(port) || v.openGateThreeAdditionalPNo === Number(port));
      if (scale) {
        this.setInputs(scale.gateThreeStatusPNo, 0);
        if (Number(value) > 0 && !this[`change-weight-${scale.inputPNo}`]) {
          this.resumeDischarge(scale);
          this.discharge(scale.inputPNo, scale, scale.gateThreeStatusPNo, Number(value));
        }
        return;
      }
      scale = this.scales.find(v => v.closeGateThreePNo === Number(port) || v.closeGateThreeAdditionalPNo === Number(port));
      if (scale && Number(value) > 1000) { // ignoring closing the gate for less than 1 sec
        this.stopDischarge(scale);
        this.setInputs(scale.gateThreeStatusPNo, 1);
        return;
      }

      const bin = this.bins.find(v => Number(port) === toNumber(v.gateOnePNo));
      if (bin) {
        const scale = this.scales.find(v => v.id === bin.scaleId);
        this.setOutput(bin.gateOnePNo, '1');

        this.timers.set(
          timerKey.device(bin.id),
          () => this.setOutput(bin.gateOnePNo, '0'),
          toNumber(value),
          `Bin ${bin.id} gate auto-off`
        );

        if (scale) {
          if (Number(value) > 10000) {
            this.scaleFeed(scale);
          } else {
            this.setWeightPlus(scale.inputPNo, Number(value)/10);
          }
        }
        return;
      }

      let tank = this.tanks.find(v => Number(port) === v.gatePNo);
      if (tank) {
        if (tank.closeStatusPNo) {
          this.timers.set(
            timerKey.device(tank.id),
            () => this.setInputs(tank.closeStatusPNo, '0'),
            500,
            `Tank ${tank.id} close status`
          );
        }
        if (tank.emptyStatusPNo) {
          this.timers.set(
            timerKey.tankEmpty(tank.id),
            () => this.setInputs(tank.emptyStatusPNo, '1'),
            2000,
            `Tank ${tank.id} empty status`
          );
        }
        return;
      }

      let pump = this.pumps.find(v => Number(port) === v.bottleGatePNo); // arrow down for bottle

      if (pump) {
        this.setOutput(pump.bottleGatePNo, '1');

        if (pump.emptyStatusPNo) {
          this.timers.set(
            timerKey.device(pump.id),
            () => this.setInputs(pump.emptyStatusPNo, '1'),
            2000,
            `Pump ${pump.id} empty status (bottle)`
          );
        }

        return;
      }
      pump = this.pumps.find(v => Number(port) === v.gatePNo); // arrow up for pump
      if (pump) {
        this.setOutput(pump.gatePNo, '1');
        this.resumeFeed(pump);
        this.pumpFeed(pump);
        if (pump.emptyStatusPNo) {
          this.setInputs(pump.emptyStatusPNo, '0');
        }

        const tank = this.tanks.find(v => pump.tankId === v.id);
        if (tank && tank.emptyStatusPNo) {
          this.timers.set(
            timerKey.device(tank.id),
            () => this.setInputs(tank.emptyStatusPNo, '0'),
            2000,
            `Tank ${tank.id} not empty (pump feeding)`
          );
        }

        return;
      }

      let belt = this.belts.find(v => Number(port) === v.onPNo);
      if (belt) {
        if (belt.onPNo === Number(port)) {
          this.setOutput(belt.onPNo, '1');
        } else {
          this.setOutput(belt.onPNo, '0');
        }
        if (value < 10000) {
          this.timers.set(
            timerKey.device(belt.id),
            () => this.setOutput(belt.onPNo, '0'),
            2000,
            `Belt ${belt.id} auto-off`
          );
        }
        return;
      }
      belt = this.belts.find(v => Number(port) === v.offPNo);
      if (belt) {
        if (belt.offPNo === Number(port)) {
          this.setOutput(belt.offPNo, '1');
          if (value < 10000) {
            this.timers.set(
              timerKey.device(belt.id),
              () => this.setOutput(belt.offPNo, '0'),
              2000,
              `Belt ${belt.id} off button release`
            );
          }
        } else {
          this.setOutput(belt.offPNo, '0');
        }
        return;
      }

      const switchDevice = this.switches.find(v => Number(port) === v.onPNo) || this.switches.find(v => Number(port) === v.offPNo);
      if (switchDevice) {
        if (switchDevice.onPNo === Number(port)) {
          this.setOutput(switchDevice.onPNo, '1');
          this.setInputs(switchDevice.statusPNo, 1);
        } else if (switchDevice.offPNo === Number(port)) {
          this.setOutput(switchDevice.offPNo, '1');
          this.setInputs(switchDevice.statusPNo, 1);
        }
        return;
      }
      const screw = this.screws.find(v => Number(port) === v.switchOnePNo) || this.screws.find(v => Number(port) === v.switchOffPNo);
      if (screw) {
        if (screw.switchOnePNo === Number(port)) {
          this.setOutput(screw.switchOffPNo, '0');
        } else if (screw.switchOffPNo === Number(port)) {
          this.setOutput(screw.switchOnePNo, '0');
        }
        return;
      }

      let mixer = this.mixers.find(v => Number(port) === v.openTiltPNo) || this.mixers.find(v => Number(port) === v.closeReturnPNo);
      if (mixer) {
        if (mixer.openTiltPNo === Number(port)) {
          this.setInputs(mixer.closeReturnStatusPNo, 0);
          this.timers.set(
            timerKey.device(mixer.id),
            () => this.setInputs(mixer.openTiltStatusPNo, 1),
            1000,
            `Mixer ${mixer.id} open tilt status`
          );
        } else {
          this.setInputs(mixer.openTiltStatusPNo, 0);
          this.timers.set(
            timerKey.device(mixer.id),
            () => this.setInputs(mixer.closeReturnStatusPNo, 1),
            1000,
            `Mixer ${mixer.id} close return status`
          );
        }
        return;
      }
      mixer = this.mixers.find(v => Number(port) === v.mixerOnPNo);
      if (mixer) {
        if (mixer.mixerOnPNo === Number(port)) {
          this.setOutput(mixer.mixerOnPNo, '1');
        } else {
          this.setOutput(mixer.mixerOnPNo, '0');
        }
        if (value < 10000) {
          this.timers.set(
            timerKey.device(mixer.id),
            () => this.setOutput(mixer.mixerOnPNo, '0'),
            2000,
            `Mixer ${mixer.id} auto-off`
          );
        }
        return;
      }
      mixer = this.mixers.find(v => Number(port) === v.mixerOffPNo);
      if (mixer) {
        if (mixer.mixerOffPNo === Number(port)) {
          this.setOutput(mixer.mixerOffPNo, '1');
          if (value < 10000) {
            this.timers.set(
              timerKey.device(mixer.id),
              () => this.setOutput(mixer.mixerOffPNo, '0'),
              2000,
              `Mixer ${mixer.id} off button release`
            );
          }
        } else {
          this.setOutput(mixer.mixerOffPNo, '0');
        }
        return;
      }

      const boot = this.boots.find(v => Number(port) === v.upPNo) || this.boots.find(v => Number(port) === v.downPNo);
      if (boot) {
        if (boot.upPNo === Number(port)) {
          this.setInputs(boot.downStatusPNo, 1);
        } else {
          this.setInputs(boot.downStatusPNo, 0);
        }
        return;
      }

    } else if (value === '0') { // close
      this.setOutput(port, value);

      let scale = this.scales.find(v => v.openGateOnePNo === Number(port));
      if (scale && (this.isWaterScale(scale) || !scale.closeGateOnePNo || (scale.switchGateTypePNo && !this.isOutputOn(scale.switchGateTypePNo)))) {
        this.setInputs(scale.gateOneStatusPNo, 1);
        this.stopDischarge(scale);
        return;
      }

      scale = this.scales.find(v => v.openGateOneAdditionalPNo === Number(port));
      if (scale && !scale.closeGateOneAdditionalPNo) {
        this.setInputs(scale.gateOneStatusPNo, 1);
        this.stopDischarge(scale);
        return;
      }

      scale = this.scales.find(v => (v.closeGateOnePNo || v.closeGateOneAdditionalPNo) === Number(port));
      if (scale) {
        this.resumeDischarge(scale);
        return;
      }

      scale = this.scales.find(v => v.openGateTwoPNo === Number(port));
      if (scale && !scale.closeGateTwoPNo) {
        this.setInputs(scale.gateTwoStatusPNo, 1);
        return;
      }

      scale = this.scales.find(v => v.openGateTwoAdditionalPNo === Number(port));
      if (scale && !scale.closeGateTwoAdditionalPNo) {
        this.setInputs(scale.gateTwoStatusPNo, 1);
        return;
      }

      scale = this.scales.find(v => (v.closeGateTwoPNo || v.closeGateTwoAdditionalPNo) === Number(port));
      if (scale) {
        this.resumeDischarge(scale);
        return;
      }

      scale = this.scales.find(v => v.openGateThreePNo === Number(port));
      if (scale && !scale.closeGateThreePNo) {
        this.setInputs(scale.gateThreeStatusPNo, 1);
        return;
      }

      scale = this.scales.find(v => v.openGateThreeAdditionalPNo === Number(port));
      if (scale && !scale.closeGateThreeAdditionalPNo) {
        this.setInputs(scale.gateThreeStatusPNo, 1);
        return;
      }

      scale = this.scales.find(v => (v.closeGateThreePNo || v.closeGateThreeAdditionalPNo) === Number(port));
      if (scale) {
        this.resumeDischarge(scale);
        return;
      }

      const switchDevice = this.switches.find(v => Number(port) === v.onPNo) || this.switches.find(v => Number(port) === v.offPNo);
      if (switchDevice) {
        if (switchDevice.onPNo === Number(port)) {
          this.setOutput(switchDevice.onPNo, '0');
          this.setInputs(switchDevice.statusPNo, 0);
        } else if (switchDevice.offPNo === Number(port)) {
          this.setOutput(switchDevice.offPNo, '0');
          this.setInputs(switchDevice.statusPNo, 0);
        }
        return;
      }

      const bin = this.bins.find(v => Number(port) === toNumber(v.gateOnePNo));
      if (bin) {
        this.setOutput(bin.gateOnePNo, '0');
        const scale = this.scales.find(v => v.id === bin.scaleId);
        if (scale) {
          this.timers.clear(timerKey.scaleWeight(scale.inputPNo));
        }
        return;
      }

      let pump = this.pumps.find(v => Number(port) === v.bottleGatePNo);
      if (pump) {
        this.stopFeed(pump);
        this.setOutput(pump.bottleGatePNo, '0');
        return;
      }
      pump = this.pumps.find(v => Number(port) === v.gatePNo);
      if (pump) {
        this.stopFeed(pump);
        this.setOutput(pump.gatePNo, '0');
        this.timers.clear(timerKey.pumpMeter(pump.meterPNo));
        return;
      }

      const mixer = this.mixers.find(v => Number(port) === v.openTiltPNo) || this.mixers.find(v => Number(port) === v.closeReturnPNo);
      if (mixer && mixer.type === MixerType.ForwardReverse) { // only for F-R mixer
        if (mixer.openTiltPNo === Number(port)) {
          this.setInputs(mixer.openTiltStatusPNo, 0);
        } else {
          this.setInputs(mixer.closeReturnStatusPNo, 0);
        }
        return;
      }

      let tank = this.tanks.find(v => Number(port) === v.gatePNo);
      if (tank && tank.closeStatusPNo) {
        this.timers.set(
          timerKey.device(tank.id),
          () => this.setInputs(tank.closeStatusPNo, '1'),
          500,
          `Tank ${tank.id} gate closed`
        );
        return;
      }

    }

  };

  stopFeed(device) {
    this.stopFeedDevices.push(device);
  };

  resumeFeed(device) {
    this.stopFeedDevices = this.stopFeedDevices.filter(v => v.id !== device.id);
  };

  stopDischarge(scale) {
    this.stopDischargeScales.push(scale);
  };

  resumeDischarge(scale) {
    this.stopDischargeScales = this.stopDischargeScales.filter(v => v.id !== scale.id);
  };

  changeWeightForDecum(scaleInputPNo, target, scaleGateClosePNo) {
    scaleInputPNo = toNumber(scaleInputPNo);
    target = toNumber(target);
    scaleGateClosePNo = toNumber(scaleGateClosePNo);
    let w = this.data.weights.split(' ');
    const actualValue = toNumber(w[scaleInputPNo]);
    const scale = this.scales.find(v => v.inputPNo === Number(scaleInputPNo));
    this[`change-weight-${scaleInputPNo}`] = true;

    this.timers.clear(timerKey.scaleWeight(scaleInputPNo));
    this.resumeDischarge(scale);

    if (actualValue <= target) {
      this.setOutput(scaleGateClosePNo, 1);
      this.setWeightStatus(scaleInputPNo, '2');
      this[`change-weight-${scaleInputPNo}`] = false;
      return;
    }

    const value = actualValue - target;
    w[scaleInputPNo] = actualValue > 0 ? actualValue - (value > 50 ? 50 : value) : 0;
    this.data.weights = w.join(' ');

    if (scale.parentScaleId) {
      const nextScale = this.scales.find(v => v.id === scale.parentScaleId);
      this.setWeightPlus(nextScale.inputPNo, value > 50 ? 50 : value);
    }

    this.setMoisture();

    this.timers.set(
      timerKey.scaleWeight(scaleInputPNo),
      () => this.changeWeightForDecum(scaleInputPNo, target, scaleGateClosePNo),
      200,
      `Decumulative scale ${scaleInputPNo} weight change`
    );
  }

  changeWeight(scaleInputPNo, secondaryBinValue = 0, primaryBinValue = 0, secondaryBinPort, primaryBinPort, firstBinSet = false) {
    scaleInputPNo = toNumber(scaleInputPNo);
    primaryBinValue = toNumber(primaryBinValue);
    secondaryBinValue = toNumber(secondaryBinValue);
    primaryBinPort = toNumber(primaryBinPort);
    secondaryBinPort = toNumber(secondaryBinPort);
    const value = primaryBinValue > secondaryBinValue ? primaryBinValue : secondaryBinValue;

    let w = this.data.weights.split(' ');
    const actualValue = toNumber(w[scaleInputPNo]);

    if (primaryBinPort && actualValue >= primaryBinValue) {
      this.setOutput(primaryBinPort, 0);
      this.setWeightStatus(scaleInputPNo, '1');
    }

    if (actualValue >= value) {
      this.setWeightStatus(scaleInputPNo, '2');
      return;
    }

    if (primaryBinPort && actualValue < primaryBinValue) {
      this.setOutput(primaryBinPort, 1);
    }
    if (secondaryBinPort && actualValue < secondaryBinValue) {
      this.setOutput(secondaryBinPort, 1);
    }

    const v = value - actualValue > 50 ? 50 : value - actualValue;
    w[scaleInputPNo] = actualValue + v;
    this.data.weights = w.join(' ');
    this.setMoisture();

    if (secondaryBinPort && actualValue >= secondaryBinValue && !firstBinSet) {
      this.setWeightStatus(scaleInputPNo, '1');
      this.setOutput(secondaryBinPort, 0);
      firstBinSet = true;
    }

    this.timers.set(
      timerKey.scaleWeight(scaleInputPNo),
      () => this.changeWeight(scaleInputPNo, secondaryBinValue, primaryBinValue, secondaryBinPort, primaryBinPort, firstBinSet),
      100,
      `Scale ${scaleInputPNo} weight change`
    );
  };

  setWeightStatus(scalePort, value = '0') {
    const w = this.data.weights.split(' ');
    const p = w[0].split('');
    p[scalePort-1] = value;
    w[0] = p.join('');
    this.data.weights = w.join(' ');
  };

  resetIndicator(scalePort) {
    this.setWeight(scalePort, 0);
  };

  changeMeter(port, value, pumpPort, emptyStatusPNo) {
    if (this.stopFeedDevices.find(v => v.meterPNo === Number(port))) {
      this.timers.set(
        timerKey.pumpMeter(port),
        () => this.changeMeter(port, value, pumpPort, emptyStatusPNo),
        2000,
        `Pump ${port} meter retry (stopped)`
      );
      return;
    }

    const pump = this.pumps.find(v => port === v.meterPNo) || {};
    const tank = this.tanks.find(v => pump.tankId === v.id);
    port = toNumber(port);
    value = toNumber(value);
    pumpPort = toNumber(pumpPort);
    emptyStatusPNo = emptyStatusPNo || pump.emptyStatusPNo;
    let meters = this.data.meters.split(' ');
    const actualValue = toNumber(meters[port]);
    const speed = value - actualValue > 10 ? 10 : 1;

    if (actualValue >= value) {
      this.setMeterStatus(port, true);

      if (pumpPort) {
        this.setOutput(pumpPort, 0);
      }

      this.timers.clear(timerKey.pumpMeter(port));
      return;
    }
    this.timers.set(
      timerKey.pumpMeter(port),
      () => {
        if (this.stopFeedDevices.find(v => v.meterPNo === Number(port))) {
          this.changeMeter(port, value, pumpPort, emptyStatusPNo);
          return;
        }
        if (pumpPort) {
          this.setOutput(pumpPort, 1);
        }
        if (emptyStatusPNo) {
          this.setInputs(emptyStatusPNo, 0);
        }
        if (tank && tank.emptyStatusPNo) {
          this.setInputs(tank.emptyStatusPNo, 0);
        }

        this.setMeter(port, toNumber(actualValue + speed));
        this.changeMeter(port, value, pumpPort, emptyStatusPNo);
      },
      100,
      `Pump ${port} meter increment`
    );
  };

  scaleFeed(scale) {
    this.setWeightPlus(scale.inputPNo, 10);

    this.timers.set(
      timerKey.scaleWeight(scale.inputPNo),
      () => this.scaleFeed(scale),
      100,
      `Scale ${scale.inputPNo} continuous feed`
    );
  }

  pumpFeed(pump) {
    let meters = this.data.meters.split(' ');
    const actualValue = toNumber(meters[pump.meterPNo]);
    this.setMeter(pump.meterPNo, toNumber(actualValue + 1));

    this.timers.set(
      timerKey.pumpMeter(pump.meterPNo),
      () => this.pumpFeed(pump),
      200,
      `Pump ${pump.meterPNo} continuous feed`
    );
  }

  setWeight(port, value = 0) {
    let w = this.data.weights.split(' ');
    w[port] = toNumber(value);
    this.data.weights = w.join(' ');
  };

  setWeightPlus(port, value) {
    let w = this.data.weights.split(' ');
    w[port] = value !== 0 ? toNumber(w[port]) + toNumber(value) : 0;
    this.data.weights = w.join(' ');
  };

  setInputs(port, value) {
    let o = this.data.inputs.replace(/\./g, '').trim().split('');

    o[port - 1] = value;

    const inputSegments = [];
    for (let i = 0; i < 48; i += 16) {
      inputSegments.push(o.slice(i, i + 16).join(''));
    }

    this.data.inputs = inputSegments.join('.');
  }

  getInputValue(port) {
    let o = this.data.inputs.replace(/\./g, '')
      .trim()
      .split('');
    return o[port - 1];
  };

  setOutput(port, value) {
    let o = this.data.outputs.replace(/\./g, '').split('');

    o[port - 1] = value;

    const outputSegments = [];
    for (let i = 0; i < 96; i += 16) {
      outputSegments.push(o.slice(i, i + 16).join(''));
    }

    this.data.outputs = outputSegments.join('.');
  }

  isOutputOn(port) {
    let o = this.data.outputs.replace(/\./g, '');

    return o[port - 1] === '1';
  }

  discharge(port, scale, gateStatusPort, time = OUTPUT_TTL, openClosePort) {
    if (this.stopDischargeScales.find(v => v.id === scale.id)) {
      this.timers.set(
        timerKey.scaleWeight(scale.inputPNo),
        () => this.discharge(port, scale, gateStatusPort, time),
        2000,
        `Scale ${scale.inputPNo} discharge retry (stopped)`
      );
      return;
    }

    let w = this.data.weights.split(' ');
    const actualValue = toNumber(w[port]);
    const nextScale = this.scales.find(v => v.id === scale.parentScaleId);
    let n = this.isWaterScale(scale) ? 10 : time < 3000 ? time/10 : 50;
    const speed = this.dischargeSpeed[scale.id] || n;

    if (time < 1000 && openClosePort) { // control flow rate
      if (Number(openClosePort) === scale.openGateOnePNo) {
        n = speed + 1;
      } else if (Number(openClosePort) === scale.closeGateOnePNo) {
        n = speed - 1;
      }

      this.dischargeSpeed[scale.id] = n;
    }

    if (!this.isScaleOpen(scale, gateStatusPort)) {
      this.timers.clear(timerKey.scaleWeight(scale.inputPNo));
      return;
    }

    if (actualValue > 0) {
      this.timers.set(
        timerKey.scaleWeight(scale.inputPNo),
        () => {
          this.setWeightPlus(scale.inputPNo, n > actualValue ? 0 : -1*n);
          if (nextScale) {
            this.setWeightPlus(nextScale.inputPNo, actualValue < n ? actualValue : n);
          }
          this.discharge(port, scale, gateStatusPort, time, openClosePort);
        },
        200,
        `Scale ${scale.inputPNo} discharge`
      );
    } else {
      this.timers.clear(timerKey.scaleWeight(scale.inputPNo));
      this.setWeightPlus(scale.inputPNo, 0);
    }
  };

  setMeterStatus(meterPort, finish = true) {
    const meters = this.data.meters;
    let status = meters.split(' ');
    let st = status[0].split('');
    st[meterPort - 1] = finish ? '1' : '0';
    status[0] = st.join('');
    this.data.meters = status.join(' ');
  };

  resetMeter(meterPort) {
    this.setMeterStatus(meterPort, false);
  }

  resetCounter(meterPort) {
    this.setMeter(meterPort, 0);
  }

  setMeter(meterPort, value) {
    let meters = this.data.meters.split(' ');
    meters[meterPort] = value;
    this.data.meters = meters.join(' ');
  }

  setAnalogInput(port, value = 0) {
    let w = this.data['analog-inputs'].split(' ');
    w[port-1] = Number(value);
    this.data['analog-inputs'] = w.join(' ');
  };

  setAnalogOutput(port, value = 0) {
    let w = this.data['analog-outputs'].split(' ');
    w[port-1] = toNumber(value);
    this.data['analog-outputs'] = w.join(' ');
  };

  animateAnalogOutput(port, value = 0) {
    let w = this.data['analog-outputs'].split(' ');
    const currentValue = toNumber(w[port-1]);

    if (currentValue < value) {
      this.setAnalogOutput(port, currentValue + 1);
    } else if (currentValue > value) {
      this.setAnalogOutput(port, currentValue - 1);
    } else {
      return;
    }

    this.timers.set(
      timerKey.analog(port),
      () => this.animateAnalogOutput(port, value),
      50,
      `Analog output ${port} animation`
    );
  };
}

export default PacSimulatorStore;
